import React, { useEffect, useMemo, useState } from "react";
import type {
  ArticleListingItem,
  ArticlesListingBlock as ArticlesListingBlockType,
  CategoryReference,
} from "@/types/content";
import { SearchIcon, ChevronRightIcon } from "@/components/icons";
import { useLocalizedTexts, t } from "@/utils/translations";
import { fixedCmsImage, responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./ArticlesListingBlock.module.css";

/**
 * In a full-width block the section's content box is at most 1120px (the
 * 1168px grid less 1.5rem padding each side): three cards of ~357px, two
 * below 1024px, one below 768px.
 */
const CARD_IMAGE_SIZES =
  "(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 360px";
/** The featured image is the 1.2fr column of 1120px, full width below 1024px. */
const FEATURED_IMAGE_SIZES = "(max-width: 1023px) 100vw, 612px";

const PAGE_SIZE = 9;
const ALL_CATEGORY = "all" as const;
type ActiveCategory = string | typeof ALL_CATEGORY;

export interface ArticlesListingBlockProps {
  block: ArticlesListingBlockType;
  className?: string;
}

interface ArticleCardProps {
  item: ArticleListingItem;
  localizedTexts: ReturnType<typeof useLocalizedTexts>;
}

const formatPublishDate = (iso: string | null | undefined, locale: string) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const ArticleCard: React.FC<ArticleCardProps> = ({ item, localizedTexts }) => {
  const { title, intro, image, author, authorImage, link, categories } = item;
  const primaryCategory = categories[0];
  const formattedDate = formatPublishDate(
    item.publishDate,
    typeof navigator !== "undefined" ? navigator.language : "en-GB"
  );

  const cardBody = (
    <>
      <div className={styles.media}>
        {image ? (
          <img
            {...responsiveCmsImage(image.url, CARD_IMAGE_SIZES, image.width)}
            alt={image.altText || title}
            className={styles.mediaImage}
            loading="lazy"
            width={image.width || undefined}
            height={image.height || undefined}
          />
        ) : (
          <div className={styles.mediaPlaceholder} aria-hidden="true" />
        )}
        {primaryCategory && (
          <span className={styles.categoryPill}>{primaryCategory.name}</span>
        )}
      </div>

      <div className={styles.body}>
        <h3 className={styles.title}>{title}</h3>
        {intro && <p className={styles.excerpt}>{intro}</p>}

        {(author || formattedDate) && (
          <div className={styles.byline}>
            {authorImage && (
              <img
                {...fixedCmsImage(authorImage.url, { width: 28 })}
                alt={authorImage.altText || author || ""}
                className={styles.avatar}
                width={authorImage.width || undefined}
                height={authorImage.height || undefined}
                loading="lazy"
              />
            )}
            <span className={styles.bylineText}>
              {author && <strong>{author}</strong>}
              {author && formattedDate && (
                <span className={styles.bylineSeparator} aria-hidden="true">
                  {" · "}
                </span>
              )}
              {formattedDate}
            </span>
          </div>
        )}
      </div>
    </>
  );

  if (link?.url) {
    return (
      <article className={styles.card}>
        <a
          href={link.url}
          className={styles.cardLink}
          target={link.target || "_self"}
          rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
          aria-label={`${t(localizedTexts, "Tracker.Articles.ReadArticle", "Read article")}: ${title}`}
        >
          {cardBody}
        </a>
      </article>
    );
  }

  return <article className={styles.card}>{cardBody}</article>;
};

interface FeaturedArticleProps {
  item: ArticleListingItem;
  localizedTexts: ReturnType<typeof useLocalizedTexts>;
}

const FeaturedArticle: React.FC<FeaturedArticleProps> = ({
  item,
  localizedTexts,
}) => {
  const { title, intro, image, author, authorImage, link, categories } = item;
  const primaryCategory = categories[0];
  const formattedDate = formatPublishDate(
    item.publishDate,
    typeof navigator !== "undefined" ? navigator.language : "en-GB"
  );

  return (
    <article className={styles.featured} data-component="featured-article">
      <div className={styles.featuredMedia}>
        {image ? (
          <img
            {...responsiveCmsImage(
              image.url,
              FEATURED_IMAGE_SIZES,
              image.width
            )}
            alt={image.altText || title}
            className={styles.featuredImage}
            loading="lazy"
            width={image.width || undefined}
            height={image.height || undefined}
          />
        ) : (
          <div className={styles.featuredPlaceholder} aria-hidden="true" />
        )}
        <span className={styles.featuredBadge}>
          {t(localizedTexts, "Tracker.ArticlesListing.Latest", "Latest")}
        </span>
      </div>

      <div className={styles.featuredBody}>
        {(primaryCategory || formattedDate) && (
          <span className={styles.featuredMeta}>
            {primaryCategory && primaryCategory.name}
            {primaryCategory && formattedDate && " · "}
            {formattedDate}
          </span>
        )}
        <h3 className={styles.featuredTitle}>{title}</h3>
        {intro && <p className={styles.featuredExcerpt}>{intro}</p>}

        {(author || authorImage) && (
          <div className={styles.featuredByline}>
            {authorImage && (
              <img
                {...fixedCmsImage(authorImage.url, { width: 36 })}
                alt={authorImage.altText || author || ""}
                className={styles.featuredAvatar}
                width={authorImage.width || undefined}
                height={authorImage.height || undefined}
                loading="lazy"
              />
            )}
            {author && (
              <span className={styles.featuredBylineText}>
                <strong>{author}</strong>
              </span>
            )}
          </div>
        )}

        {link?.url && (
          <a
            href={link.url}
            className={styles.featuredCta}
            target={link.target || "_self"}
            rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
          >
            {t(
              localizedTexts,
              "Tracker.ArticlesListing.ReadStory",
              "Read the story"
            )}
            <ChevronRightIcon
              className={styles.featuredCtaIcon}
              aria-hidden="true"
            />
          </a>
        )}
      </div>
    </article>
  );
};

interface FilterPillProps {
  isActive: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}

const FilterPill: React.FC<FilterPillProps> = ({
  isActive,
  label,
  count,
  onClick,
}) => (
  <button
    type="button"
    className={`${styles.pill} ${isActive ? styles.pillActive : ""}`}
    onClick={onClick}
    aria-pressed={isActive}
  >
    {label}
    {typeof count === "number" && (
      <span className={styles.pillCount}>{count}</span>
    )}
  </button>
);

export const ArticlesListingBlock: React.FC<ArticlesListingBlockProps> = ({
  block,
  className,
}) => {
  const localizedTexts = useLocalizedTexts();
  const {
    heading,
    searchPlaceholder,
    showFeatured,
    categories,
    items,
    isMainTitle = false,
  } = block;

  // The editor can mark this heading as the page h1; the styling stays the same.
  const HeadingTag = isMainTitle ? "h1" : "h2";

  const [activeCategory, setActiveCategory] =
    useState<ActiveCategory>(ALL_CATEGORY);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeCategory, searchQuery]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const categoryMatch =
        activeCategory === ALL_CATEGORY ||
        item.categories.some((c) => c.id === activeCategory);

      if (!categoryMatch) return false;
      if (!normalizedQuery) return true;

      const haystack = `${item.title} ${item.intro ?? ""}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [items, activeCategory, normalizedQuery]);

  const isUnfilteredView =
    activeCategory === ALL_CATEGORY && normalizedQuery === "";
  const featuredItem =
    showFeatured && isUnfilteredView && filteredItems.length > 0
      ? filteredItems[0]
      : null;
  const gridItems = featuredItem ? filteredItems.slice(1) : filteredItems;

  const visibleItems = gridItems.slice(0, visibleCount);
  const hasMore = visibleItems.length < gridItems.length;

  const allCount = useMemo(() => {
    if (!normalizedQuery) return items.length;
    return items.filter((item) => {
      const haystack = `${item.title} ${item.intro ?? ""}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    }).length;
  }, [items, normalizedQuery]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      if (normalizedQuery) {
        const haystack = `${item.title} ${item.intro ?? ""}`.toLowerCase();
        if (!haystack.includes(normalizedQuery)) continue;
      }
      for (const cat of item.categories) {
        counts.set(cat.id, (counts.get(cat.id) ?? 0) + 1);
      }
    }
    return counts;
  }, [items, normalizedQuery]);

  const containerClasses = [styles.section, className]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={containerClasses}
      data-component="articles-listing-block"
    >
      {heading && <HeadingTag className={styles.heading}>{heading}</HeadingTag>}

      <div
        className={styles.filterBar}
        role="group"
        aria-label={t(
          localizedTexts,
          "Tracker.ArticlesListing.FilterArticles",
          "Filter articles"
        )}
      >
        {categories.length > 0 && (
          <>
            <span className={styles.filterLabel}>
              {t(
                localizedTexts,
                "Tracker.ArticlesListing.BrowseBy",
                "Browse by"
              )}
            </span>
            <FilterPill
              isActive={activeCategory === ALL_CATEGORY}
              label={t(localizedTexts, "Tracker.ArticlesListing.All", "All")}
              count={allCount}
              onClick={() => setActiveCategory(ALL_CATEGORY)}
            />
            {categories.map((cat: CategoryReference) => (
              <FilterPill
                key={cat.id}
                isActive={activeCategory === cat.id}
                label={cat.name}
                count={categoryCounts.get(cat.id) ?? 0}
                onClick={() => setActiveCategory(cat.id)}
              />
            ))}
          </>
        )}

        <label className={styles.search}>
          <SearchIcon className={styles.searchIcon} aria-hidden="true" />
          <span className={styles.visuallyHidden}>
            {t(
              localizedTexts,
              "Tracker.ArticlesListing.SearchPlaceholder",
              "Search stories"
            )}
          </span>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={
              searchPlaceholder ||
              t(
                localizedTexts,
                "Tracker.ArticlesListing.SearchPlaceholder",
                "Search stories"
              )
            }
            className={styles.searchInput}
          />
        </label>
      </div>

      {featuredItem && (
        <FeaturedArticle item={featuredItem} localizedTexts={localizedTexts} />
      )}

      {!featuredItem && visibleItems.length === 0 ? (
        <div className={styles.empty}>
          <h3 className={styles.emptyTitle}>
            {t(
              localizedTexts,
              "Tracker.ArticlesListing.EmptyTitle",
              "Nothing here yet"
            )}
          </h3>
          <p className={styles.emptyBody}>
            {t(
              localizedTexts,
              "Tracker.ArticlesListing.EmptyBody",
              "Try a different category or clear your search."
            )}
          </p>
        </div>
      ) : (
        visibleItems.length > 0 && (
          <div className={styles.grid} role="list">
            {visibleItems.map((item) => (
              <div key={item.id} className={styles.gridItem} role="listitem">
                <ArticleCard item={item} localizedTexts={localizedTexts} />
              </div>
            ))}
          </div>
        )
      )}

      {hasMore && (
        <div className={styles.loadMoreWrap}>
          <button
            type="button"
            className={styles.loadMore}
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          >
            {t(
              localizedTexts,
              "Tracker.ArticlesListing.ShowMore",
              "Show more stories"
            )}
            <ChevronRightIcon
              className={styles.loadMoreIcon}
              aria-hidden="true"
            />
          </button>
        </div>
      )}
    </section>
  );
};

export default ArticlesListingBlock;
